import Switch from '@/components/Switch/Switch';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import styled from '@emotion/styled';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CtaFields, DescriptionField, LogoField, TitleField } from './Fields';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 461px;
    min-width: 461px;
    background-color: white;
    padding: 16px;
    border-right: 1px solid ${theme.colors.black[300]};
    box-shadow:
        0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.11),
        0.5px 1.3px 1.8px -0.6px rgba(187, 187, 187, 0.18),
        1.1px 2.7px 3.8px -1.2px rgba(187, 187, 187, 0.26);
    /*
& > *:not(:first-child) {
margin-block-end: 24px;
}
*/
`;

const PageTitleCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${theme.colors.black[300]};
`;

const PageTitle = styled.div`
    color: ${theme.colors.secondary.main};
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: -0.48px;
`;

const PageSubtitle = styled.div`
    color: ${theme.colors.black[500]};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.48px;
`;

const ItemCardTop = styled.div<{ composite?: boolean; expanded?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px;

    ${({ composite = false, expanded = false }) =>
        composite &&
        (expanded
            ? `
    padding: 0 0 24px;
    border-bottom: 1px solid ${theme.colors.black[300]};  
  `
            : `
 padding: 0;
 `)}
`;

const ItemCardFrameDisabled = styled.div`
    display: flex;
    flex-direction: column;
    border: none;
    border-radius: 8px;
    background-color: ${theme.colors.black[100]};
`;

const ItemCardCompositeFrame = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 12px;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 8px;
`;

const ItemCardHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const ItemCardTitle = styled.div`
    color: ${theme.colors.secondary.main};
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: -0.02em;
`;

const ItemCardSubtitle = styled.div`
    color: ${theme.colors.black[600]};
    font-family: Manrope;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.02em;
`;

export const SideBar = () => {
    const [customizeToggle, setCustomizeToggle] = useState(false);
    const { resetField } = useFormContext();
    const isEmbedEnabled = useFlag('is-embed-campaign-enabled', false);

    const resetVideoPageCustomization = useCallback(() => {
        resetField('cta_enabled');
        resetField('cta_text');
        resetField('cta_url');
        resetField('cta_background_color');
        resetField('description');
        resetField('logo_src');
        resetField('title');
    }, [resetField]);

    const onCustomizeToggle = useCallback(
        (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setCustomizeToggle(checked);
            if (!checked) {
                resetVideoPageCustomization();
            }
        },
        [resetVideoPageCustomization]
    );

    return (
        <Container>
            <PageTitleCard>
                <PageTitle>Display your campaign</PageTitle>
                <PageSubtitle>
                    {isEmbedEnabled
                        ? 'Embed your campaign on your website, or display it on a D-ID hosted video page.'
                        : 'Display your campaign on a D-ID hosted video page.'}
                </PageSubtitle>
            </PageTitleCard>

            {isEmbedEnabled && (
                <ItemCardFrameDisabled>
                    <ItemCardTop>
                        <ItemCardHeader>
                            <ItemCardTitle>Embed campaign on your website</ItemCardTitle>
                        </ItemCardHeader>
                        <ItemCardSubtitle>
                            Once you create your campaign you’ll get a code snippet to embed on your website.
                        </ItemCardSubtitle>
                    </ItemCardTop>
                </ItemCardFrameDisabled>
            )}

            <ItemCardCompositeFrame>
                <ItemCardTop composite={true} expanded={customizeToggle}>
                    <ItemCardHeader>
                        <ItemCardTitle>Customize your video page</ItemCardTitle>
                        <Switch value={customizeToggle} onChange={onCustomizeToggle} />
                    </ItemCardHeader>
                    <ItemCardSubtitle>Add your campaign assets to your video page</ItemCardSubtitle>
                </ItemCardTop>

                {customizeToggle && (
                    <>
                        <TitleField />
                        <DescriptionField />
                        <LogoField />
                        <CtaFields />
                    </>
                )}
            </ItemCardCompositeFrame>
        </Container>
    );
};
